import { useEffect, useState, useRef } from 'react';
import type { VerticalLine } from '@/modules/shared/DataLayers/types';
import { groupMarkersBySection, findFirstMarkerInSection } from '@/modules/shared/DataLayers/utils';

export interface UseBenchmarkLinesReturn {
  benchmarkLineBySection: Record<string, string | null>;
  setBenchmarkLineBySection: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
}

// Simple global storage that persists across component switches
const globalBenchmarkCache: Record<string, string | null> = {};

/**
 * Manages benchmark line selection per section
 * Automatically sets first marker as benchmark for each section
 * Persists across component changes using global cache
 */
export function useBenchmarkLines(verticalLines: VerticalLine[]): UseBenchmarkLinesReturn {
  // Track benchmark line per section (sectionKey -> lineId)
  // Initialize from cache
  const [benchmarkLineBySection, setBenchmarkLineBySection] = useState<Record<string, string | null>>(() => ({
    ...globalBenchmarkCache,
  }));

  // Sync to cache whenever state changes
  const prevStateRef = useRef(benchmarkLineBySection);
  useEffect(() => {
    if (prevStateRef.current !== benchmarkLineBySection) {
      Object.assign(globalBenchmarkCache, benchmarkLineBySection);
      prevStateRef.current = benchmarkLineBySection;
    }
  }, [benchmarkLineBySection]);

  // Automatically set first marker as benchmark for each section
  useEffect(() => {
    const updates: Record<string, string> = {};

    // Group markers by section
    const markersBySection = groupMarkersBySection(verticalLines);

    // For each section, check if we need to set a default benchmark
    Object.entries(markersBySection).forEach(([sectionKey, markers]) => {
      const currentBenchmark = benchmarkLineBySection[sectionKey];

      // If no benchmark is set and we have markers, set the first one as benchmark
      if (!currentBenchmark && markers.length > 0) {
        const firstMarker = findFirstMarkerInSection(markers);
        if (firstMarker) {
          updates[sectionKey] = firstMarker.id;
        }
      }

      // DO NOT auto-reset if benchmark is missing from current verticalLines
      // The marker might belong to a different component that's not currently rendered
      // Actual cleanup is handled in DataLayerDashboardContainer when markers are deleted
    });

    // Apply updates if any
    if (Object.keys(updates).length > 0) {
      setBenchmarkLineBySection((prev) => ({ ...prev, ...updates }));
    }
  }, [verticalLines, benchmarkLineBySection]);

  return {
    benchmarkLineBySection,
    setBenchmarkLineBySection,
  };
}
